import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  isArray,
  isFunction,
  isString,
  isStringArray,
} from '../util/typechecks'

type NoLiterals<O extends object> = {
  [K in keyof O]: O[K] extends string
    ? string
    : O[K] extends number
    ? number
    : O[K] extends boolean
    ? boolean
    : O[K]
}

// tslint:disable-next-line:no-empty-interface
export interface DefaultMessages {}

export type MessageId<T extends object> = keyof T extends never
  ? string
  : keyof T
export type MessageIdWithDefault = [MessageId<DefaultMessages>, string]
export type MessageIdWithCallback = [MessageId<DefaultMessages>, object]
export type MessageIdWithTransformer = [
  MessageId<DefaultMessages>,
  (value: string) => string
]
export type MessageIdWithCallbackAndTransformer = [
  MessageId<DefaultMessages>,
  object,
  (value: string) => string
]

export type Messages = {
  [name: string]:
    | MessageId<DefaultMessages>
    | MessageIdWithDefault
    | MessageIdWithCallback
    | MessageIdWithTransformer
    | MessageIdWithCallbackAndTransformer
    | Messages
}

export type UseTranslateReturn<T> = {
  [K in keyof T]: T[K] extends MessageIdWithCallbackAndTransformer
    ? (args?: Partial<NoLiterals<T[K][1]>>) => string
    : T[K] extends MessageIdWithTransformer
    ? string
    : T[K] extends MessageIdWithCallback
    ? (args?: Partial<NoLiterals<T[K][1]>>) => string
    : T[K] extends Messages
    ? UseTranslateReturn<T[K]>
    : string
}

/**
 * Create a translation object based on react-intls message format and formatMessage function
 * @param messages The messages to translate
 * @param key The translations will run again if this key changes. If you don't provida a (dynamic) key,
 * the translations will only run once, even if messages changes (unless locale changes).
 */
export function useTranslate<T extends Messages>(
  messages: T,
  key = 'lesmiserables'
) {
  const intl = useIntl()
  const keyRef = useRef(key)
  const localeRef = useRef(intl.locale)

  const translateMessageId = useCallback(
    (id: MessageId<DefaultMessages>) =>
      intl.formatMessage({
        id,
      }),
    [intl]
  )
  const translateMessageIdWithDefault = useCallback(
    ([id, defaultMessage]: MessageIdWithDefault) =>
      intl.formatMessage({
        id,
        defaultMessage,
      }),
    [intl]
  )
  const translateMessageIdWithCallback = useCallback(
    ([id, values]: MessageIdWithCallback) =>
      (args?: typeof values) =>
        intl.formatMessage(
          {
            id,
          },
          {
            ...values,
            ...args,
          } as any
        ),
    [intl]
  )

  const translateMessageIdWithCallbackAndTransformer = useCallback(
    ([id, values, transformer]: MessageIdWithCallbackAndTransformer) =>
      (args?: typeof values) =>
        transformer(translateMessageIdWithCallback([id, values])(args)),
    [translateMessageIdWithCallback]
  )

  const runTranslation = useCallback(
    (msgs?: Messages) => {
      const toTranslate = msgs ?? messages
      return Object.entries(toTranslate).reduce((acc, [key, value]) => {
        // This value is a message and a default message
        if (isStringArray(value))
          acc[key as keyof T] = translateMessageIdWithDefault(
            value as MessageIdWithDefault
          ) as any
        // The value is not just a message
        else if (isArray(value))
          if (isFunction(value[1]))
            // The second array item is a function, which means this is a message and transformer function
            acc[key as keyof T] = value[1](translateMessageId(value[0])) as any
          // There are three items in array and second isn't a function, so third must be a transformer function
          else if (value.length === 3)
            acc[key as keyof T] = translateMessageIdWithCallbackAndTransformer(
              value as MessageIdWithCallbackAndTransformer
            ) as any
          // Still an array, so that means this must be a message with values
          else
            acc[key as keyof T] = translateMessageIdWithCallback(
              value as MessageIdWithCallback
            ) as any
        // This is just a message id
        else if (isString(value))
          acc[key as keyof T] = translateMessageId(
            value as MessageId<DefaultMessages>
          ) as any
        // Last option, it is a nested translations object
        else acc[key as keyof T] = runTranslation(value) as any

        return acc
      }, {} as UseTranslateReturn<T>)
    },
    [
      messages,
      translateMessageId,
      translateMessageIdWithCallback,
      translateMessageIdWithCallbackAndTransformer,
      translateMessageIdWithDefault,
    ]
  )

  const [translated, setTranslated] = useState<UseTranslateReturn<T>>(() =>
    runTranslation()
  )

  useEffect(() => {
    if (localeRef.current === intl.locale && isEqual(keyRef.current, key))
      return
    localeRef.current = intl.locale
    keyRef.current = key

    const trans = runTranslation()
    setTranslated(trans)
  }, [intl.locale, key, runTranslation])

  return translated
}
